import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import { PrimaryButton } from "../components/Button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Play, X } from "lucide-react";
import uploadIcon from "../assets/upload-icon.svg";
import trainerPlaceholder from "../assets/trainer-placeholder.png";
import { getTrainerProfile, updateTrainerProfile } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

type Gender = "M" | "F";

const TrainerEditProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [gender, setGender] = useState<Gender>("M");
  const [profile, setProfile] = useState({
    _id: "",
    name: "",
    email: "",
    aboutMe: "",
    age: "",
    creditsPerHour: "",
    phoneNumber: "",
    whatsappNumber: "",
    dob: "",
    sex: "",
    instaLink: "",
    facebookLink: "",
    tiktokLink: "",
    maxSessionsPerDay: "",
    cooldownPeriod: "",
    thresholdPeriod: "",
  });

  // Server-provided media keys
  const [serverVideos, setServerVideos] = useState<string[]>([]);
  const [serverPhotos, setServerPhotos] = useState<string[]>([]);
  const [serverProfilePic, setServerProfilePic] = useState<string>("");

  // Pending file uploads
  const [newProfilePic, setNewProfilePic] = useState<File | null>(null);
  const [newTrainingPhotos, setNewTrainingPhotos] = useState<File[]>([]);
  const [newTrainingVideos, setNewTrainingVideos] = useState<File[]>([]);

  // Deletions to send to backend
  const [deleteKeys, setDeleteKeys] = useState<string[]>([]);

  // Full screen preview
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [fullScreenImageError, setFullScreenImageError] = useState(false);

  // Fetch trainer profile and prepopulate form
  useEffect(() => {
    const fetchTrainerProfile = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const trainerData = await getTrainerProfile(user.id);

        // Prepopulate form with existing data (exclude media fields)
        console.log('Server response trainerData:', trainerData);
        console.log('trainerData keys:', Object.keys(trainerData));

        setProfile({
          _id: trainerData._id || "",
          name: trainerData.name || "",
          email: trainerData.email || "",
          aboutMe: trainerData.aboutMe || "",
          age: trainerData.age || "",
          creditsPerHour: trainerData.creditsPerHour?.toString() || "",
          phoneNumber: trainerData.phoneNumber || "",
          whatsappNumber: trainerData.whatsappNumber || "",
          dob: trainerData.dob || "",
          sex: trainerData.sex || "",
          instaLink: trainerData.instaLink || "",
          facebookLink: trainerData.facebookLink || "",
          tiktokLink: trainerData.tiktokLink || "",
          maxSessionsPerDay: trainerData.maxSessionsPerDay?.toString() || "",
          cooldownPeriod: trainerData.cooldownPeriod?.toString() || "",
          thresholdPeriod: trainerData.thresholdPeriod?.toString() || "",
        });

        // Media from server
        setServerVideos(Array.isArray(trainerData.trainingVideoLinks) ? trainerData.trainingVideoLinks : []);
        setServerPhotos(Array.isArray(trainerData.trainingPhotos) ? trainerData.trainingPhotos : []);
        setServerProfilePic(trainerData.profilePic || "");

        // Set gender based on sex field
        if (trainerData.sex) {
          setGender(trainerData.sex === "Male" ? "M" : "F");
        }
      } catch (err) {
        console.error('Error fetching trainer profile:', err);
        setError('Failed to load trainer profile');
      } finally {
        setLoading(false);
      }
    };

    fetchTrainerProfile();
  }, [user?.id, location.key]);

  const setField = (k: keyof typeof profile, v: string) => setProfile((p) => ({ ...p, [k]: v }));
  const removeMediaKey = (key: string) => {
    setDeleteKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setServerVideos((prev) => prev.filter((k) => k !== key));
    setServerPhotos((prev) => prev.filter((k) => k !== key));
    if (serverProfilePic === key) setServerProfilePic("");
  };

  const onSelectProfilePic: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0] || null;
    console.log('Selected profile pic:', file);
    setNewProfilePic(file);
  };
  const onSelectPhotos: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = Array.from(e.target.files || []);
    console.log('Selected training photos:', files);
    setNewTrainingPhotos(files);
  };
  const onSelectVideos: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = Array.from(e.target.files || []);
    console.log('Selected training videos:', files);
    setNewTrainingVideos(files);
  };

  const removeNewPhoto = (index: number) => {
    setNewTrainingPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewVideo = (index: number) => {
    setNewTrainingVideos(prev => prev.filter((_, i) => i !== index));
  };

  // Create preview URLs for selected files
  const createPreviewUrl = (file: File): string => {
    return URL.createObjectURL(file);
  };

  // Helper function to check if URL is a video
  const isVideoUrl = (url: string): boolean => {
    if (!url) return false;
    // Check for video file extensions (ignore query params/fragments)
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.m4v'];
    let pathToCheck = url;
    try {
      pathToCheck = new URL(url).pathname;
    } catch {
      pathToCheck = url.split('?')[0].split('#')[0];
    }
    const isVideoFile = videoExtensions.some(ext => pathToCheck.toLowerCase().endsWith(ext));
    // Check for video hosting platforms
    const isVideoPlatform = url.includes("youtube.com") || url.includes("youtu.be") || url.includes("instagram.com");
    return isVideoFile || isVideoPlatform;
  };

  // Handle media click to open in full screen
  const handleMediaClick = (mediaUrl: string) => {
    if (isVideoUrl(mediaUrl)) {
      // For external video platforms, open in new tab
      if (mediaUrl.includes("youtube.com") || mediaUrl.includes("youtu.be") || mediaUrl.includes("instagram.com")) {
        window.open(mediaUrl, '_blank');
      } else {
        // Local video files - open in full screen modal with video player
        setFullScreenImage(mediaUrl);
      }
    } else {
      // Open image in full screen
      setFullScreenImage(mediaUrl);
    }
  };

  const closeFullScreen = () => {
    setFullScreenImage(null);
    setFullScreenImageError(false);
  };

  // Clean up object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      // Cleanup object URLs to prevent memory leaks
      newTrainingPhotos.forEach(file => {
        const url = URL.createObjectURL(file);
        URL.revokeObjectURL(url);
      });
      newTrainingVideos.forEach(file => {
        const url = URL.createObjectURL(file);
        URL.revokeObjectURL(url);
      });
      if (newProfilePic) {
        const url = URL.createObjectURL(newProfilePic);
        URL.revokeObjectURL(url);
      }
    };
  }, [newTrainingPhotos, newTrainingVideos, newProfilePic]);

  const save = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!profile.email || !profile.email.trim()) {
        setError("Email is required.");
        setSaving(false);
        return;
      }

      // Prepare data for API - only include fields that should be sent
      console.log('Current profile state:', profile);
      console.log('Profile keys:', Object.keys(profile));
      console.log('profile.trainingPhotos:', (profile as any).trainingPhotos);
      console.log('profile.trainingVideos:', (profile as any).trainingVideos);

      const profileData: any = {
        _id: profile._id,
        name: profile.name,
        email: profile.email,
        aboutMe: profile.aboutMe,
        age: profile.age,
        phoneNumber: profile.phoneNumber,
        whatsappNumber: profile.whatsappNumber,
        dob: profile.dob ? new Date(profile.dob) : undefined,
        sex: gender === "M" ? "Male" : "Female",
        instaLink: profile.instaLink,
        facebookLink: profile.facebookLink,
        tiktokLink: profile.tiktokLink,
        // Convert string numbers back to numbers where needed
        creditsPerHour: profile.creditsPerHour ? parseInt(profile.creditsPerHour) : undefined,
        maxSessionsPerDay: profile.maxSessionsPerDay ? parseInt(profile.maxSessionsPerDay) : undefined,
        cooldownPeriod: profile.cooldownPeriod ? parseInt(profile.cooldownPeriod) : undefined,
        thresholdPeriod: profile.thresholdPeriod ? parseInt(profile.thresholdPeriod) : undefined,
      };

      console.log('profileData after creation:', profileData);
      console.log('profileData keys after creation:', Object.keys(profileData));
      console.log('profileData.trainingPhotos after creation:', profileData.trainingPhotos);
      console.log('profileData.trainingVideos after creation:', profileData.trainingVideos);

      // Attach files for upload
      console.log('Files to upload:');
      console.log('newProfilePic:', newProfilePic);
      console.log('newTrainingPhotos:', newTrainingPhotos);
      console.log('newTrainingVideos:', newTrainingVideos);

      if (newProfilePic) {
        console.log('Adding profile pic:', newProfilePic.name);
        profileData.profilePic = newProfilePic;
      }
      if (newTrainingPhotos.length > 0) {
        console.log('Adding training photos:', newTrainingPhotos.map(f => f.name));
        profileData.trainingPhotos = newTrainingPhotos;
      }
      if (newTrainingVideos.length > 0) {
        console.log('Adding training videos:', newTrainingVideos.map(f => f.name));
        profileData.trainingVideos = newTrainingVideos;
      }
      if (deleteKeys.length > 0) profileData.deleteMedia = JSON.stringify(deleteKeys);

      console.log('Final profileData being sent:', profileData);
      console.log('Checking for file fields in profileData:');
      console.log('profileData.trainingPhotos:', profileData.trainingPhotos);
      console.log('profileData.trainingVideos:', profileData.trainingVideos);
      console.log('profileData.profilePic:', profileData.profilePic);

      // Verify files are still File objects
      if (profileData.profilePic) {
        console.log('profileData.profilePic is File:', profileData.profilePic instanceof File);
        console.log('profileData.profilePic name:', profileData.profilePic.name);
      }
      if (profileData.trainingPhotos) {
        console.log('profileData.trainingPhotos is Array:', Array.isArray(profileData.trainingPhotos));
        console.log('profileData.trainingPhotos length:', profileData.trainingPhotos.length);
        profileData.trainingPhotos.forEach((f: File, i: number) => {
          console.log(`trainingPhotos[${i}] is File:`, f instanceof File);
          console.log(`trainingPhotos[${i}] name:`, f.name);
        });
      }
      if (profileData.trainingVideos) {
        console.log('profileData.trainingVideos is Array:', Array.isArray(profileData.trainingVideos));
        console.log('profileData.trainingVideos length:', profileData.trainingVideos.length);
        profileData.trainingVideos.forEach((f: File, i: number) => {
          console.log(`trainingVideos[${i}] is File:`, f instanceof File);
          console.log(`trainingVideos[${i}] name:`, f.name);
        });
      }

      await updateTrainerProfile(profileData);
      navigate("/trainer/profile");
    } catch (err) {
      console.error('Error saving trainer profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    // SINGLE SCROLLER: 100dvh + overflow-y-auto (no nested scroll areas)
    <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto overscroll-contain">
      <div className="mx-auto w-full min-h-full">
        {/* Header */}
        {/* Header – same look as Trainer Profile */}
        <div className="relative overflow-hidden bg-[linear-gradient(157.07deg,#3a3a3a_0%,#252525_81.65%)]">
          <Navbar onBack={() => navigate("/trainer/profile")} />

          {/* content */}
          <div className="mx-auto w-full px-5 pb-20 text-white relative z-10">
            {/* Title + action with trainer image */}
            <div className="relative">
              {/* right-side hero photo */}
              <img
                src={serverProfilePic || (newProfilePic ? createPreviewUrl(newProfilePic) : trainerPlaceholder)}
                alt="Trainer"
                className="absolute right-0 top-0 h-[176px] object-contain pointer-events-none select-none bottom-[0px] z-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = trainerPlaceholder;
                }}
              />

              <div className="relative z-10">
                <h1 className="text-[#EB2726] text-[28px] leading-[28px] font-extrabold italic">
                  {loading ? (
                    'LOADING...'
                  ) : error ? (
                    'ERROR'
                  ) : (
                    (() => {
                      const fullName = profile.name || 'TRAINER';
                      const nameParts = fullName.toUpperCase().split(' ');
                      const firstName = nameParts[0] || '';
                      const lastName = nameParts.slice(1).join(' ') || '';

                      return (
                        <>
                          <div>{firstName}</div>
                          {lastName && <div>{lastName}</div>}
                        </>
                      );
                    })()
                  )}
                </h1>
                <label className="mt-3 inline-block text-xs text-[#EB2726] underline underline-offset-2 cursor-pointer relative z-20">
                  <input type="file" accept="image/*" className="hidden" onChange={onSelectProfilePic} />
                  CHANGE PROFILE PIC
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white px-5 py-6 space-y-6">
          <p className="text-[#B0B0B0] text-xs font-extrabold tracking-[0.05em] uppercase">// Edit Profile</p>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div>
            <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-2">Email</label>
            <Input
              value={profile.email}
              onChange={(e) => setField("email", e.target.value)}
              className="rounded-none border-[#B1B1B1] bg-white h-[48px]"
              type="email"
              required
            />
          </div>

          <div>
            <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-2">Phone Number</label>
            <Input value={profile.phoneNumber} onChange={(e) => setField("phoneNumber", e.target.value)} className="rounded-none border-[#B1B1B1] bg-white h-[48px]" />
          </div>

          <div>
            <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-2">WhatsApp Number</label>
            <Input value={profile.whatsappNumber} onChange={(e) => setField("whatsappNumber", e.target.value)} className="rounded-none border-[#B1B1B1] bg-white h-[48px]" />
          </div>

          <div>
            <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-2">Credits per hour</label>
            <Input value={profile.creditsPerHour} onChange={(e) => setField("creditsPerHour", e.target.value)} className="rounded-none border-[#B1B1B1] bg-white h-[48px]" />
            <p className="text-xs text-[#717171] mt-1">
              Credits per hour can be <span className="font-bold">changed by admin only</span>
            </p>
          </div>

          <div>
            <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-2">Your Name</label>
            <Input value={profile.name} onChange={(e) => setField("name", e.target.value)} className="rounded-none border-[#B1B1B1] bg-white h-[48px]" />
          </div>

          <div>
            <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-2">About You</label>
            <Textarea value={profile.aboutMe} onChange={(e) => setField("aboutMe", e.target.value)} className="rounded-none border-[#B1B1B1] bg-white min-h-[100px]" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-2">How old are you?</label>
              <Input value={profile.age} onChange={(e) => setField("age", e.target.value)} className="rounded-none border-[#B1B1B1] bg-white h-[48px]" />
            </div>
            <div>
              <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-2">Your gender</label>
              <div className="flex gap-2">
                {(["M", "F"] as Gender[]).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGender(g)}
                    className={[
                      "flex-1 h-[48px] border text-sm font-medium rounded-none",
                      gender === g ? "border-[#D62422] bg-white text-black" : "border-[#B1B1B1] bg-white text-[#717171]",
                    ].join(" ")}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-4">Upload your training videos</label>
            <div className="relative">
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={onSelectVideos}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className="w-full h-[48px] border-[1px] border-[#B1B1B1] bg-white flex items-center justify-between px-4">
                <span className="text-[#9A9A9A] text-[16px] truncate">
                  {newTrainingVideos.length > 0
                    ? `${newTrainingVideos.length} video${newTrainingVideos.length > 1 ? 's' : ''} selected`
                    : "Upload File"
                  }
                </span>
                <img src={uploadIcon} alt="Upload" className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div>
            <label className="block font-extrabold text-[12px] tracking-[0.05em] uppercase mb-4">Upload your training images</label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onSelectPhotos}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className="w-full h-[48px] border-[1px] border-[#B1B1B1] bg-white flex items-center justify-between px-4">
                <span className="text-[#9A9A9A] text-[16px] truncate">
                  {newTrainingPhotos.length > 0
                    ? `${newTrainingPhotos.length} image${newTrainingPhotos.length > 1 ? 's' : ''} selected`
                    : "Upload File"
                  }
                </span>
                <img src={uploadIcon} alt="Upload" className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Media tiles: show server media + newly selected media */}
          <div className="space-y-4">
            {/* Server Profile Pic */}
            {serverProfilePic && (
              <div className="relative">
                <div
                  className="h-[180px] w-full bg-[#e9e9e9] border border-[#DEDEDE] overflow-hidden cursor-pointer"
                  onClick={() => handleMediaClick(serverProfilePic)}
                >
                  <img
                    src={serverProfilePic}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                    }}
                  />
                  <div className="hidden w-full h-full flex items-center justify-center text-xs text-gray-600">
                    Profile Pic: {serverProfilePic}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeMediaKey(serverProfilePic);
                  }}
                  className="absolute bottom-3 right-3 bg-[#EB2726] text-white px-3 py-1 rounded text-xs font-bold z-10"
                >
                  DELETE
                </button>
              </div>
            )}

            {/* New Profile Pic */}
            {newProfilePic && (
              <div className="relative">
                <div
                  className="h-[180px] w-full bg-[#e9e9e9] border border-[#DEDEDE] overflow-hidden relative cursor-pointer"
                  onClick={() => handleMediaClick(createPreviewUrl(newProfilePic))}
                >
                  <img
                    src={createPreviewUrl(newProfilePic)}
                    alt="New Profile Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    NEW
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewProfilePic(null);
                  }}
                  className="absolute bottom-3 right-3 bg-[#EB2726] text-white px-3 py-1 rounded text-xs font-bold z-10"
                >
                  DELETE
                </button>
              </div>
            )}

            {/* Server Photos */}
            {serverPhotos.map((key) => (
              <div key={key} className="relative">
                <div
                  className="h-[180px] w-full bg-[#e9e9e9] border border-[#DEDEDE] overflow-hidden cursor-pointer"
                  onClick={() => handleMediaClick(key)}
                >
                  <img
                    src={key}
                    alt="Training Image"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                    }}
                  />
                  <div className="hidden w-full h-full flex items-center justify-center text-xs text-gray-600">
                    Image: {key}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeMediaKey(key);
                  }}
                  className="absolute bottom-3 right-3 bg-[#EB2726] text-white px-3 py-1 rounded text-xs font-bold z-10"
                >
                  DELETE
                </button>
              </div>
            ))}

            {/* New Photos */}
            {newTrainingPhotos.map((file, index) => (
              <div key={`new-photo-${index}`} className="relative">
                <div
                  className="h-[180px] w-full bg-[#e9e9e9] border border-[#DEDEDE] overflow-hidden relative cursor-pointer"
                  onClick={() => handleMediaClick(createPreviewUrl(file))}
                >
                  <img
                    src={createPreviewUrl(file)}
                    alt={`New Image: ${file.name}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    NEW
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNewPhoto(index);
                  }}
                  className="absolute bottom-3 right-3 bg-[#EB2726] text-white px-3 py-1 rounded text-xs font-bold z-10"
                >
                  DELETE
                </button>
              </div>
            ))}

            {/* Server Videos */}
            {serverVideos.map((key) => (
              <div key={key} className="relative">
                <div
                  className="h-[180px] w-full bg-[#e9e9e9] border border-[#DEDEDE] overflow-hidden relative cursor-pointer"
                  onClick={() => handleMediaClick(key)}
                >
                  <video
                    src={key}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLVideoElement).style.display = 'none';
                      (e.target as HTMLVideoElement).nextElementSibling!.classList.remove('hidden');
                    }}
                  />
                  <div className="hidden w-full h-full flex items-center justify-center text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <span>Video: {key}</span>
                      <div className="w-6 h-6 rounded-full bg-[#EB2726] grid place-items-center text-white">
                        <Play className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                      <Play className="w-6 h-6 text-white ml-1" />
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeMediaKey(key);
                  }}
                  className="absolute bottom-3 right-3 bg-[#EB2726] text-white px-3 py-1 rounded text-xs font-bold z-10"
                >
                  DELETE
                </button>
              </div>
            ))}

            {/* New Videos */}
            {newTrainingVideos.map((file, index) => (
              <div key={`new-video-${index}`} className="relative">
                <div
                  className="h-[180px] w-full bg-[#e9e9e9] border border-[#DEDEDE] overflow-hidden relative cursor-pointer"
                  onClick={() => handleMediaClick(createPreviewUrl(file))}
                >
                  <video
                    src={createPreviewUrl(file)}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    NEW
                  </div>
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                      <Play className="w-6 h-6 text-white ml-1" />
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNewVideo(index);
                  }}
                  className="absolute bottom-3 right-3 bg-[#EB2726] text-white px-3 py-1 rounded text-xs font-bold z-10"
                >
                  DELETE
                </button>
              </div>
            ))}
          </div>

          <div className="pt-4 pb-6">
            <PrimaryButton
              label={saving ? "SAVING..." : "SAVE CHANGES"}
              onClick={save}
              className="!w-full"
              disabled={saving || loading}
            />
          </div>
        </div>
      </div>

      {/* Full screen image/video modal */}
      {fullScreenImage && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <button
            onClick={closeFullScreen}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          {isVideoUrl(fullScreenImage) ? (
            // Video player for video files
            <div className="w-full h-full flex items-center justify-center px-4">
              <video
                src={fullScreenImage}
                controls
                autoPlay
                className="max-w-full max-h-full"
                onError={() => {
                  console.error('Failed to load video in full screen:', fullScreenImage);
                  setFullScreenImageError(true);
                }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          ) : !fullScreenImageError ? (
            <img
              src={fullScreenImage}
              alt="Full screen view"
              className="max-w-full max-h-full object-contain"
              onClick={closeFullScreen}
              onError={(e) => {
                // If image fails to load in full screen, show error state
                console.error('Failed to load image in full screen:', fullScreenImage);
                setFullScreenImageError(true);
              }}
            />
          ) : (
            <div className="text-white text-center px-8">
              <p className="text-lg mb-4">Failed to load media</p>
              <p className="text-sm text-gray-400 mb-4">The file may be corrupted or the file path is incorrect.</p>
              <button
                onClick={closeFullScreen}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrainerEditProfile;
